import { EditOutlined as EditOutlinedIcon, VisibilityOutlined as VisibilityOutlinedIcon } from 'components/icons';
import { CallForActionButtonTooltip } from 'components/buttons/CallForActionButton';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { TimecardListItemData, TimecardStatusType } from 'types/timecards';
import { t } from 'utils/getTranslations';
import { isMatterInFinalState } from 'utils/matterState';
import { useHasPermissions } from 'utils/permissionUtil';
import TimecardActionButtons, { ACTION_LINK_CLASS, isExpensableTimecard } from '../TimecardActionButtons';

type Props = {
    timecard: TimecardListItemData;
};

/** Whether a timecard can be opened from a list: a draft on a closed case cannot. */
export const canOpenTimecard = (timecard: TimecardListItemData): boolean =>
    !(
        isMatterInFinalState(timecard.matter.matterStateTypeCode) &&
        timecard.timecardStatusTypeCode === TimecardStatusType.DRAFT
    );

/** A 32px box an action occupies, kept even when that action is absent from this row. */
function ActionSlot({ children }: { children?: React.ReactNode }): JSX.Element {
    return <span className="flex size-8 shrink-0 items-center justify-center">{children}</span>;
}

/**
 * The row's actions: open, edit, and whatever {@link TimecardActionButtons} adds for this timecard
 * (expense, duplicate, finish, tracking, and the duplicate/delete menu).
 *
 * Every one of those is optional per row, so the first three get a **reserved slot** and the group is laid out
 * from the left edge of a fixed-width column: view, edit and expense therefore sit at the same
 * position in every row, instead of sliding sideways as their neighbours come and go. The slot for
 * the expense button has to be reserved out here, because the button itself belongs to the shared
 * action group — hence {@link isExpensableTimecard}, so the condition is written once.
 *
 * The kebab is the exception: it belongs at the row's end, not beside whatever precedes it, so the menu
 * trigger itself takes the leftover width as margin and lands against the cell's far edge. It is picked
 * by its own `data-slot`, not as the group's last child: while the menu is open, Base UI inserts hidden
 * focus-guard spans after the trigger, and a last-child rule would move onto them.
 *
 * Open and edit are gated exactly as the card list gated its two text links: a draft on a closed case
 * offers neither, and edit needs the backend's own `isEditable`.
 */
export function TimecardRowActions({ timecard }: Props): JSX.Element {
    const hasCreateExpensePermission = useHasPermissions(['CREATE.EXPENSES']);
    const canOpen = canOpenTimecard(timecard);
    // A blocked case, or a draft on a closed one, replaces the whole group with a single tag — so
    // there are no slots to line up on those rows.
    const showsTagInsteadOfActions = timecard.matter.isCurrentlyBlocked || !canOpen;
    const reservesExpenseSlot =
        !showsTagInsteadOfActions && !(isExpensableTimecard(timecard) && hasCreateExpensePermission);

    return (
        <div className="flex w-full items-center gap-1 [&>[data-slot=dropdown-menu-trigger]]:ml-auto">
            <ActionSlot>
                {canOpen ? (
                    <CallForActionButtonTooltip title={t('timecards.view-timecard')}>
                        <LinkWithOrigin
                            to={`/timecards/${timecard.id}`}
                            aria-label={t('timecards.view-timecard')}
                            className={ACTION_LINK_CLASS}
                        >
                            <VisibilityOutlinedIcon className="text-[20px]" />
                        </LinkWithOrigin>
                    </CallForActionButtonTooltip>
                ) : null}
            </ActionSlot>
            <ActionSlot>
                {canOpen && timecard.isEditable ? (
                    <CallForActionButtonTooltip title={t('timecards.edit-timecard')}>
                        <LinkWithOrigin
                            to={`/timecards/${timecard.id}/edit`}
                            aria-label={t('timecards.edit-timecard')}
                            className={ACTION_LINK_CLASS}
                        >
                            <EditOutlinedIcon className="text-[20px]" />
                        </LinkWithOrigin>
                    </CallForActionButtonTooltip>
                ) : null}
            </ActionSlot>
            {reservesExpenseSlot ? <ActionSlot /> : null}
            <TimecardActionButtons timecard={timecard} showDuplicateAction />
        </div>
    );
}
